import { TrashIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  addAiProjectShare,
  AiProjectShare,
  AiShareableGroup,
  getAiProjectShares,
  getAiShareableGroups,
  getInternalUsers,
  removeAiProjectShare,
} from 'services'
import { Button, Select2 } from 'stories/components'

interface SharePanelProps {
  projectId: number
  /** Only the owner may change who a project is shared with; everyone else sees the list read-only. */
  isOwner: boolean
  /** Reflects the company-wide switch, which makes the named grants redundant while it is on. */
  isSharedWithCompany: boolean
}

/**
 * Who a project is shared with, beyond its owner: named people, and user groups.
 *
 * Sharing with a group stores the group, not a snapshot of its members - so someone added to the
 * group later gets the project too, which is the reason to share with a group at all.
 */
export function SharePanel({ projectId, isOwner, isSharedWithCompany }: SharePanelProps) {
  const [shares, setShares] = useState<AiProjectShare[]>([])
  const [users, setUsers] = useState<{ name: string; email: string }[]>([])
  const [groups, setGroups] = useState<AiShareableGroup[]>([])
  const [subjectType, setSubjectType] = useState<'user' | 'group'>('user')
  const [subject, setSubject] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = () => {
    getAiProjectShares(projectId)
      .then((data) => setShares(data || []))
      .catch(() => {})
  }

  useEffect(() => {
    reload()
    if (!isOwner) return
    // Only the owner can add a share, so only the owner needs the pickers' data.
    getInternalUsers()
      .then((data) => setUsers(data || []))
      .catch(() => {})
    // Not the admin group endpoint: that one needs the User.Group permission, which a project
    // owner generally does not have.
    getAiShareableGroups()
      .then((data) => setGroups(data || []))
      .catch(() => {})
  }, [projectId, isOwner])

  // Anyone already shared with drops out of the picker, so the list only offers grants that change something.
  const userOptions = useMemo(() => {
    const taken = new Set(shares.filter((s) => s.subjectType === 'user').map((s) => (s.email || '').toLowerCase()))
    return users
      .filter((user) => user.email && !taken.has(user.email.toLowerCase()))
      .reduce(
        (acc, user) => ({ ...acc, [user.email]: user.name ? `${user.name} (${user.email})` : user.email }),
        {} as Record<string, string>,
      )
  }, [users, shares])

  const groupOptions = useMemo(() => {
    const taken = new Set(shares.filter((s) => s.subjectType === 'group').map((s) => s.groupId))
    return groups
      .filter((group) => !taken.has(group.id))
      .reduce(
        (acc, group) => ({
          ...acc,
          [group.id]: `${group.name} (${group.memberCount} member${group.memberCount === 1 ? '' : 's'})`,
        }),
        {} as Record<string, string>,
      )
  }, [groups, shares])

  const onAdd = async () => {
    if (!subject) {
      toast(subjectType === 'group' ? 'Pick a group to share with.' : 'Pick a person to share with.', {
        type: 'error',
      })
      return
    }
    setBusy(true)
    try {
      await addAiProjectShare(projectId,
        subjectType === 'group' ? { subjectType, groupId: Number(subject) } : { subjectType, email: subject },
      )
      setSubject('')
      reload()
    } finally {
      setBusy(false)
    }
  }

  const onRemove = async (share: AiProjectShare) => {
    if (!window.confirm(`Stop sharing this project with ${share.name}?`)) return
    await removeAiProjectShare(projectId, share.id)
    reload()
  }

  return (
    <div>
      <p className="text-sm font-medium text-gray-700">Shared with</p>

      {!shares.length && (
        <p className="text-xs text-gray-500 mt-1">
          Not shared with anyone yet. Add a person or a group below, or use the company-wide switch above.
        </p>
      )}

      {shares.length > 0 && (
        <ul className="mt-2 space-y-1">
          {shares.map((share) => (
            <li
              key={share.id}
              className="flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm"
            >
              {share.subjectType === 'group' ? (
                <UserGroupIcon className="w-4 h-4 shrink-0 text-gray-400" />
              ) : (
                <UserIcon className="w-4 h-4 shrink-0 text-gray-400" />
              )}
              <span className="min-w-0 flex-1 truncate text-gray-800">{share.name}</span>
              {share.subjectType === 'group' && (
                <span className="shrink-0 text-xs text-gray-500">
                  {share.memberCount || 0} member{share.memberCount === 1 ? '' : 's'}
                </span>
              )}
              {isOwner && (
                <button
                  className="shrink-0 p-1 text-gray-400 hover:text-red-600"
                  title="Remove"
                  onClick={() => onRemove(share)}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOwner && (
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <div className="w-28">
            <Select2
              id="ai-project-share-type"
              title="Share with"
              options={{ user: 'Person', group: 'Group' }}
              value={subjectType}
              hasDefaultOption={false}
              onChange={(value: string) => {
                setSubjectType(value === 'group' ? 'group' : 'user')
                setSubject('')
              }}
            />
          </div>
          <div className="flex-1 min-w-[12rem]">
            <Select2
              id="ai-project-share-subject"
              title={subjectType === 'group' ? 'Group' : 'Person'}
              options={subjectType === 'group' ? groupOptions : userOptions}
              value={subject}
              defaultOptionText={subjectType === 'group' ? 'Select a group' : 'Select a person'}
              onChange={(value: string) => setSubject(value)}
            />
          </div>
          <Button size="sm" disabled={busy} onClick={onAdd}>
            Share
          </Button>
        </div>
      )}

      {isSharedWithCompany && (
        <p className="text-xs text-gray-500 mt-2">
          This project is shared with everyone in the company, so these grants add nothing until that is turned off.
        </p>
      )}
    </div>
  )
}
